import { createSlice } from "@reduxjs/toolkit";

import {
  fetchAllEquipments,
  fetchEquipmentById,
  createEquipment,
  fetchEquipmentTotal,
} from "./equipmentActions.js";

const initialState = {
  totalCount: { value: null, loading: false, error: null, requestId: null },
  // ============================================================
  // ALL EQUIPMENT RECORDS
  // ============================================================

  allEquipments: [],

  // ============================================================
  // CURRENT / SELECTED EQUIPMENT
  // ============================================================

  currentEquipment: null,

  // ============================================================
  // PAGINATION
  // ============================================================

  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },

  // ============================================================
  // GENERAL FETCHING
  // ============================================================

  loading: false,

  // ============================================================
  // CREATE EQUIPMENT
  // ============================================================

  creating: false,

  createSuccess: false,

  // ============================================================
  // ERRORS
  // ============================================================

  error: null,
};

const equipmentSlice = createSlice({
  name: "equipment",

  initialState,

  // ============================================================
  // NORMAL REDUCERS
  // ============================================================

  reducers: {
    // Clear API errors
    clearEquipmentErrors: (state) => {
      state.error = null;
    },

    // Clear currently opened equipment
    resetCurrentEquipment: (state) => {
      state.currentEquipment = null;
    },

    // Reset create state
    resetCreateEquipmentState: (state) => {
      state.creating = false;
      state.createSuccess = false;
      state.error = null;
    },

    // Completely reset equipment state if needed
    resetEquipmentState: () => initialState,
  },

  // ============================================================
  // ASYNC ACTIONS
  // ============================================================

  extraReducers: (builder) => {
    builder
      .addCase(fetchEquipmentTotal.pending, (state, action) => {
        state.totalCount = { value: null, loading: true, error: null, requestId: action.meta.requestId };
      })
      .addCase(fetchEquipmentTotal.fulfilled, (state, action) => {
        if (state.totalCount.requestId !== action.meta.requestId) return;
        state.totalCount.value = action.payload;
        state.totalCount.loading = false;
        state.totalCount.requestId = null;
      })
      .addCase(fetchEquipmentTotal.rejected, (state, action) => {
        if (state.totalCount.requestId !== action.meta.requestId) return;
        state.totalCount.loading = false;
        state.totalCount.error = action.meta.aborted ? null : action.payload || 'Unable to load equipment total';
        state.totalCount.requestId = null;
      })

      // ============================================================
      // 1. FETCH ALL EQUIPMENTS
      // ============================================================

      .addCase(fetchAllEquipments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchAllEquipments.fulfilled, (state, action) => {
        state.loading = false;

        state.allEquipments = action.payload.data || [];

        state.pagination = action.payload.pagination || {
          total: 0,
          page: 1,
          limit: 20,
          totalPages: 0,
        };
      })

      .addCase(fetchAllEquipments.rejected, (state, action) => {
        state.loading = false;

        state.error = action.payload || "Failed to fetch equipments";
      })

      // ============================================================
      // 2. FETCH EQUIPMENT BY ID
      // ============================================================

      .addCase(fetchEquipmentById.pending, (state) => {
        state.loading = true;

        state.error = null;
      })

      .addCase(fetchEquipmentById.fulfilled, (state, action) => {
        state.loading = false;

        state.currentEquipment = action.payload.data || null;
      })

      .addCase(fetchEquipmentById.rejected, (state, action) => {
        state.loading = false;

        state.currentEquipment = null;

        state.error = action.payload || "Failed to fetch equipment details";
      })

      // ============================================================
      // 3. CREATE EQUIPMENT
      // ============================================================

      .addCase(createEquipment.pending, (state) => {
        state.creating = true;

        state.createSuccess = false;

        state.error = null;
      })

      .addCase(createEquipment.fulfilled, (state, action) => {
        state.creating = false;

        state.createSuccess = true;

        const newEquipment = action.payload.data;

        if (newEquipment) {
          // Add new equipment to beginning of list
          state.allEquipments.unshift(newEquipment);

          // Store newly created equipment as current equipment
          state.currentEquipment = newEquipment;

          // Update pagination total locally
          state.pagination.total += 1;
        }
      })

      .addCase(createEquipment.rejected, (state, action) => {
        state.creating = false;

        state.createSuccess = false;

        state.error = action.payload || "Failed to add equipment";
      });
  },
});

export const {
  clearEquipmentErrors,
  resetCurrentEquipment,
  resetCreateEquipmentState,
  resetEquipmentState,
} = equipmentSlice.actions;

export default equipmentSlice.reducer;
